import { Controller } from "@hotwired/stimulus"

/**
 * Централизованный контроллер для управления состоянием страницы расчета зарплаты
 * Объединяет всю логику состояния и UI обновления в одном месте
 */
export default class extends Controller {
  static targets = [
    // Основные секции
    "workDaysCard",
    "salaryTable", 
    "requestPayoutsTable",
    "totalAmount",
    
    // Отображение данных
    "workedDaysDisplay",
    "plannedDaysDisplay", 
    "plannedDaysInput",
    "fixedSalaryAmount",
    "personalProfitAmount",
    "progressiveProfitAmount",
    "subordinatesProfitAmount",
    "warningAlert",
    
    // Дополнительные элементы
    "additionalItem",
    "additionalItemsContainer"
  ]

  static values = {
    // Базовые данные пользователя
    userId: Number,
    monthPayrollId: Number,
    userRole: String,
    isDepHead: Boolean,
    
    // Зарплатные данные
    fixedSalary: Number,
    profitPercent: Number,
    departmentProfitPercent: Number,
    
    // Рабочие дни
    workedDays: Number,
    plannedDays: Number,
    
    // Финансовые данные
    totalMonthProfit: Number,
    personalProfit: Number,
    progressiveProfit: Number,
    subordinatesProfit: Number,
    
    // Дополнительные элементы (JSON)
    additionalItems: Array
  }

  // Объект состояния
  state = {
    // Рабочие дни
    workDays: {
      worked: 0,
      planned: 0
    },
    
    // Базовые зарплатные компоненты
    salary: {
      fixed: 0,
      personal_profit: 0,
      progressive_profit: 0,
      subordinates_profit: 0
    },
    
    // Дополнительные элементы зарплаты
    additionalItems: [],
    
    // Общие финансовые данные
    finance: {
      total_month_profit: 0
    },
    
    // UI состояния
    ui: {
      loading: false,
      errors: []
    }
  }

  connect() {
    console.log("Salary state controller connected!")
    this.initializeState()
    this.renderAll()
  }

  // ===== ИНИЦИАЛИЗАЦИЯ СОСТОЯНИЯ =====
  
  initializeState() {
    // Инициализируем состояние из значений контроллера
    this.state.workDays = {
      worked: this.workedDaysValue || 0,
      planned: this.plannedDaysValue || 0
    }
    
    this.state.salary = {
      fixed: this.calculateFixedSalaryAmount(),
      personal_profit: this.personalProfitValue || 0,
      progressive_profit: this.progressiveProfitValue || 0,
      subordinates_profit: this.subordinatesProfitValue || 0
    }
    
    // Безопасное получение дополнительных элементов с обработкой ошибок
    try {
      this.state.additionalItems = this.additionalItemsValue || []
    } catch (error) {
      console.error("Error parsing additional items:", error)
      this.state.additionalItems = []
    }
    
    this.state.finance = {
      total_month_profit: this.totalMonthProfitValue || 0
    }
    
    console.log("Initial state:", this.state)
  }

  // ===== ВЫЧИСЛЕНИЯ =====
  
  calculateFixedSalaryAmount() {
    const baseSalary = this.fixedSalaryValue || 0
    const worked = this.state.workDays.worked
    const planned = this.state.workDays.planned
    
    if (planned === 0 || worked < 0 || planned < 0) return 0
    
    // Ограничиваем отработанные дни планируемыми (не более 100% зарплаты по умолчанию)
    const effectiveWorked = Math.min(worked, planned)
    return (baseSalary / planned) * effectiveWorked
  }
  
  calculateTotalSalary() {
    // debugger;
    let total = parseFloat(this.state.salary.fixed)
    
    // Добавляем профиты для не-сотрудников
    if (this.userRoleValue !== 'employee') {
      total += this.state.salary.personal_profit
      total += this.state.salary.progressive_profit
    }
    
    // Добавляем доходы от подчиненных для руководителей отделов
    if (this.isDepHeadValue) {
      total += this.state.salary.subordinates_profit
    }
    
    // Добавляем дополнительные элементы
    this.state.additionalItems.forEach(item => {
      if (item.type === 'profit_percent') {
        total += this.state.finance.total_month_profit * (parseFloat(item.percent) / 100.0)
      } else {
        total += parseFloat(item.amount || 0)
      }
    })
    console.log("Calculated total salary:", total)
    
    return total
  }

  // ===== ОБНОВЛЕНИЕ СОСТОЯНИЯ =====
  
  updateWorkDays(worked, planned) {
    this.state.workDays.worked = worked
    this.state.workDays.planned = planned
    
    // Пересчитываем фиксированную зарплату
    this.state.salary.fixed = this.calculateFixedSalaryAmount()
    
    this.renderWorkDays()
    this.renderSalaryTable()
  }
  
  updateAdditionalItems(items) {
    this.state.additionalItems = items
    this.renderAdditionalItems()
    this.renderSalaryTable()
  }
  
  addAdditionalItem(item) {
    // Валидация данных элемента
    console.log("Adding additional item:", item)
    if (!item || typeof item !== 'object') {
      console.error('Invalid item data:', item)
      return
    }
    
    if (!item.title || item.title.trim() === '') {
      console.error('Item must have a title')
      return
    }
    
    if (item.type === 'profit_percent') {
      const percent = parseFloat(item.percent)
      if (isNaN(percent) || percent < 0 || percent > 100) {
        console.error('Invalid percentage value:', item.percent)
        return
      }
    } else {
      const amount = parseFloat(item.amount)
      if (isNaN(amount) || amount < 0) {
        console.error('Invalid amount value:', item.amount)
        return
      }
    }
    
    this.state.additionalItems.push(item)
    this.renderAdditionalItems()
    this.renderSalaryTable()
  }
  
  // ===== ОТРИСОВКА UI =====
  
  renderAll() {
    this.renderWorkDays()
    this.renderSalaryTable()
    this.renderAdditionalItems()
    this.renderTotalAmount()
  }
  
  renderWorkDays() {
    // Обновляем отображение количества дней
    if (this.hasWorkedDaysDisplayTarget) {
      this.workedDaysDisplayTarget.textContent = this.state.workDays.worked
    }
    
    if (this.hasPlannedDaysDisplayTarget) {
      this.plannedDaysDisplayTarget.textContent = this.state.workDays.planned
    }
    
    if (this.hasPlannedDaysInputTarget) {
      this.plannedDaysInputTarget.value = this.state.workDays.planned
    }
    
    // Показываем/скрываем предупреждение о несовпадении дней
    this.renderWorkDaysWarning()
  }
  
  renderWorkDaysWarning() {
    const showWarning = this.state.workDays.worked !== this.state.workDays.planned
    
    if (this.hasWarningAlertTarget) {
      if (showWarning) {
        this.warningAlertTarget.style.display = 'block'
        this.warningAlertTarget.innerHTML = `
          <i class="fas fa-exclamation-triangle me-2"></i>
          Не совпадает количество запланированных смен (${this.state.workDays.planned}) 
          и количество фактически отработанных смен (${this.state.workDays.worked}).
        `
      } else {
        this.warningAlertTarget.style.display = 'none'
      }
    }
  }
  
  renderSalaryTable() {
    // Обновляем фиксированную зарплату
    if (this.hasFixedSalaryAmountTarget) {
      this.fixedSalaryAmountTarget.innerHTML = `<strong>${this.formatCurrency(this.state.salary.fixed)}</strong>`
    }
    
    // Обновляем персональный профит (если есть цель для отображения)
    if (this.hasPersonalProfitAmountTarget) {
      this.personalProfitAmountTarget.textContent = this.formatCurrency(this.state.salary.personal_profit)
    }
    
    // Обновляем прогрессивный профит (если есть цель для отображения)
    if (this.hasProgressiveProfitAmountTarget) {
      this.progressiveProfitAmountTarget.textContent = this.formatCurrency(this.state.salary.progressive_profit)
    }
    
    // Обновляем профит от подчиненных (если есть цель для отображения)
    if (this.hasSubordinatesProfitAmountTarget) {
      this.subordinatesProfitAmountTarget.textContent = this.formatCurrency(this.state.salary.subordinates_profit)
    }
    
    // Обновляем итоговую сумму
    this.renderTotalAmount()
  }
  
  renderTotalAmount() {
    if (this.hasTotalAmountTarget) {
      const total = this.calculateTotalSalary()
      this.totalAmountTarget.textContent = this.formatCurrency(total)
    }
  }
  
  renderAdditionalItems() {
    if (!this.hasAdditionalItemsContainerTarget) return
    
    // Удаляем существующие дополнительные элементы
    this.additionalItemTargets.forEach(item => item.remove())
    
    // Добавляем новые элементы
    this.state.additionalItems.forEach((item, index) => {
      const row = this.createAdditionalItemRow(item, index)
      this.insertAdditionalItemRow(row)
    })
  }
  
  createAdditionalItemRow(item, index) {
    console.log("Creating additional item row:", item, index)
    const row = document.createElement('tr')
    row.dataset.salaryStateTarget = 'additionalItem'
    row.dataset.itemIndex = index
    
    let amountDisplay = ''
    if (item.type === 'profit_percent') {
      const calculatedAmount = this.state.finance.total_month_profit * (parseFloat(item.percent) / 100.0)
      amountDisplay = `${item.percent}% = ${this.formatCurrency(calculatedAmount)}`
    } else {
      amountDisplay = this.formatCurrency(item.amount)
    }
    
    row.innerHTML = `
      <td>
        ${item.title}
        ${item.comment ? `<div class="small text-muted">${item.comment}</div>` : ''}
      </td>
      <td class="text-end">${amountDisplay}</td>
      <td>
        <button type="button" class="btn btn-sm btn-outline-danger"
                data-action="click->salary-state#removeAdditionalItem"
                data-user-payout-id="${item.id}">
          <i class="fas fa-trash"></i> Удалить
        </button>
      </td>
    `
    
    return row
  }
  
  insertAdditionalItemRow(row) {
    const tbody = this.salaryTableTarget.querySelector('tbody')
    const totalRow = tbody.querySelector('.table-success') // Строка ИТОГО
    
    if (totalRow) {
      tbody.insertBefore(row, totalRow)
    } else {
      tbody.appendChild(row)
    }
  }

  // ===== ОБРАБОТЧИКИ СОБЫТИЙ =====
  
  // Обновление количества запланированных дней
  async updatePlannedDays(event) {
    event.preventDefault()
    
    const form = event.target
    const formData = new FormData(form)
    const newPlannedDays = parseInt(formData.get('days_count'))
    
    this.setLoading(true)
    
    try {
      const response = await this.fetchWithCSRF(form.action, {
        method: 'POST',
        body: formData
      })
      
      const data = await response.json()
      
      if (data.success) {
        // Обновляем состояние с новыми данными от сервера
        this.updateStateFromServerData(data)
        this.showNotification(data.message || 'Количество дней обновлено', 'success')
      } else {
        throw new Error(data.error || 'Ошибка обновления')
      }
    } catch (error) {
      console.error('Error updating planned days:', error)
      this.showNotification('Произошла ошибка при обновлении', 'error')
    } finally {
      this.setLoading(false)
    }
  }
  
  // Добавление дополнительного элемента зарплаты
  async addSalaryItem(event) {
    event.preventDefault()
    
    const form = event.target
    const formData = new FormData(form)
    
    this.setLoading(true)
    
    try {
      const response = await this.fetchWithCSRF(form.action, {
        method: 'POST',
        body: formData
      })
      
      const data = await response.json()
      
      if (data.success) {
        // Добавляем новый элемент в состояние
        this.state.additionalItems.push(data.user_payout)
        
        // Динамически обновляем интерфейс
        this.renderAdditionalItems()
        this.renderTotalAmount()
        
        // Очищаем форму
        form.reset()
        
        // Закрываем dropdown
        this.closeDropdown(form)
        
        this.showNotification('Элемент добавлен', 'success')
      } else {
        throw new Error(data.error || 'Ошибка добавления')
      }
    } catch (error) {
      console.error('Error adding salary item:', error)
      this.showNotification('Произошла ошибка при добавлении', 'error')
    } finally {
      this.setLoading(false)
    }
  }
  
  // Удаление дополнительного элемента по ID (новый метод)
  async removeAdditionalItem(event) {
    const button = event.target.closest('button')
    const userPayoutId = button.dataset.userPayoutId
    
    if (!confirm('Удалить эту строку?')) return
    
    this.setLoading(true)
    
    try {
      const url = `/month_payrolls/${this.monthPayrollIdValue}/remove_salary_item?user_id=${this.userIdValue}&user_payout_id=${userPayoutId}`
      
      const response = await this.fetchWithCSRF(url, {
        method: 'DELETE'
      })
      
      const data = await response.json()
      
      if (data.success) {
        // Удаляем элемент из состояния
        this.state.additionalItems = this.state.additionalItems.filter(item => item.id != userPayoutId)
        
        // Удаляем DOM элемент
        const row = button.closest('tr')
        if (row) {
          row.remove()
        }
        
        // Пересчитываем итого
        this.renderTotalAmount()
        
        this.showNotification('Элемент удален', 'success')
      } else {
        throw new Error(data.error || 'Ошибка удаления')
      }
    } catch (error) {
      console.error('Error removing salary item:', error)
      this.showNotification('Произошла ошибка при удалении', 'error')
    } finally {
      this.setLoading(false)
    }
  }

  // Удаление дополнительного элемента по индексу (старый метод - можно оставить для совместимости)
  async removeAdditionalItemByIndex(event) {
    const button = event.target.closest('button')
    const index = parseInt(button.dataset.itemIndex)
    
    if (!confirm('Удалить эту строку?')) return
    
    this.setLoading(true)
    
    try {
      // Здесь должен быть URL для удаления элемента
      const url = `/month_payrolls/${this.monthPayrollIdValue}/remove_salary_item?user_id=${this.userIdValue}&item_index=${index}`
      
      const response = await this.fetchWithCSRF(url, {
        method: 'DELETE'
      })
      
      const data = await response.json()
      
      if (data.success) {
        this.removeAdditionalItemByIndexLocal(index)
        this.showNotification('Элемент удален', 'success')
      } else {
        throw new Error(data.error || 'Ошибка удаления')
      }
    } catch (error) {
      console.error('Error removing salary item:', error)
      this.showNotification('Произошла ошибка при удалении', 'error')
    } finally {
      this.setLoading(false)
    }
  }
  
  removeAdditionalItemByIndexLocal(index) {
    if (index < 0 || index >= this.state.additionalItems.length) {
      console.error('Invalid item index:', index)
      return
    }
    
    this.state.additionalItems.splice(index, 1)
    this.renderAdditionalItems() 
    this.renderSalaryTable()
  }

  // ===== ВСПОМОГАТЕЛЬНЫЕ МЕТОДЫ =====
  
  async fetchWithCSRF(url, options = {}) {
    const csrfToken = document.querySelector('[name="csrf-token"]').content
    
    return fetch(url, {
      ...options,
      headers: {
        'X-CSRF-Token': csrfToken,
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json',
        ...options.headers
      }
    })
  }
  
  setLoading(loading) {
    this.state.ui.loading = loading
    // Можно добавить индикаторы загрузки
  }
  
  showNotification(message, type = 'info') {
    // Создаем уведомление Bootstrap alert
    const alertClass = type === 'error' ? 'alert-danger' : 
                      type === 'success' ? 'alert-success' : 'alert-info'
    
    const alertHtml = `
      <div class="alert ${alertClass} alert-dismissible fade show position-fixed" 
           style="top: 20px; right: 20px; z-index: 1050; min-width: 300px;" role="alert">
        ${message}
        <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
      </div>
    `
    
    document.body.insertAdjacentHTML('beforeend', alertHtml)
    
    // Автоматически скрываем через 5 секунд
    setTimeout(() => {
      const alert = document.querySelector('.alert-dismissible:last-of-type')
      if (alert) alert.remove()
    }, 5000)
  }
  
  closeDropdown(form) {
    const dropdown = form.closest('.dropdown-menu')
    if (dropdown) {
      const dropdownToggle = dropdown.previousElementSibling
      if (dropdownToggle && dropdownToggle.classList.contains('dropdown-toggle')) {
        // Используем Bootstrap API для закрытия dropdown
        const bsDropdown = bootstrap.Dropdown.getInstance(dropdownToggle)
        if (bsDropdown) {
          bsDropdown.hide()
        } else {
          // Fallback: простой клик
          dropdownToggle.click()
        }
      }
    }
  }
  
  formatCurrency(amount) {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount).replace('RUB', '₽')
  }
  
  // ===== ПУБЛИЧНЫЕ МЕТОДЫ ДЛЯ ДРУГИХ КОНТРОЛЛЕРОВ =====
  
  // Метод для обновления состояния извне (например, из work_days_controller)
  updateState(newState) {
    this.state = { ...this.state, ...newState }
    this.renderAll()
  }
  
  // Обновление рабочих дней от календарного контроллера
  updateWorkDaysFromCalendar(workedDays, plannedDays) {
    console.log("Updating work days from calendar:", { workedDays, plannedDays })
    this.updateWorkDays(workedDays, plannedDays)
  }
  
  // Получение текущего состояния
  getState() {
    return { ...this.state }
  }
  
  // Метод для внешнего вызова пересчета
  recalculateAll() {
    this.renderAll()
  }
  
  // ===== ОБНОВЛЕНИЕ СОСТОЯНИЯ =====
  
  updateStateFromServerData(serverData) {
    console.log("Updating state from server data:", serverData)
    
    // Обновляем рабочие дни
    if (serverData.worked_days !== undefined && serverData.planned_days !== undefined) {
      this.state.workDays.worked = serverData.worked_days
      this.state.workDays.planned = serverData.planned_days
    }
    
    // Обновляем зарплатные компоненты
    if (serverData.fixed_salary_amount !== undefined) {
      this.state.salary.fixed = serverData.fixed_salary_amount
    }
    
    if (serverData.total_personal_profit !== undefined) {
      this.state.salary.personal_profit = serverData.total_personal_profit
    }
    
    if (serverData.total_progressive_profit !== undefined) {
      this.state.salary.progressive_profit = serverData.total_progressive_profit
    }
    
    if (serverData.subordinates_profit !== undefined) {
      this.state.salary.subordinates_profit = serverData.subordinates_profit
    }
    
    if (serverData.total_month_profit !== undefined) {
      this.state.finance.total_month_profit = serverData.total_month_profit
    }
    
    // Перерендериваем все компоненты
    this.renderAll()
  }
}
